'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { Text } from '../text/text';
import { VisuallyHidden } from '../visually-hidden/visually-hidden';
import { GleamRootContext } from './context/gleam-root.context';
import { gleamLabelRecipe } from './styles/gleam-label.styles';
import type { GleamLabelPropsInterface } from './types/gleam-label.types';

/**
 * @since 10.0.0
 */

export const GleamLabel = forwardRef<HTMLSpanElement, GleamLabelPropsInterface>(
  (props: GleamLabelPropsInterface, forwardedRef) => {
    const {
      appearance = 'ellipsis',
      tone = 'default',
      onImpact = false,
      count,
      countLabel = 'notifications',
      maxCount = 99,
      maxSuffix = false,
      css: cssProp,
      renderDot,
      ...rest
    } = props;

    const { hasMultipleChildren, floatingOffset } = useContext(GleamRootContext);

    const value = useMemo(() => {
      if (maxCount === 0 || count <= maxCount) {
        return count;
      }
      return maxSuffix ? `${maxCount}+` : maxCount;
    }, [count, maxSuffix, maxCount]);

    const visualContent =
      appearance === 'dot' && renderDot
        ? renderDot({ tone, onImpact, count, announce: false })
        : value;
    const announcement = countLabel ? `${value} ${countLabel}` : 'notifications';

    return (
      <Text
        ref={forwardedRef}
        data-c11n-component="Gleam.Label"
        data-c11n-version={__C11N_VERSION__}
        css={css.raw(
          gleamLabelRecipe.raw({
            appearance,
            floating: hasMultipleChildren,
            floatingComponent: floatingOffset?.component,
            floatingOffset: floatingOffset?.size,
            onImpact,
            tone,
          }),
          cssProp,
        )}
        {...rest}
        className={cx('c11n_gleam__label', rest.className)}
      >
        <>
          <span className="c11n" aria-hidden>
            {visualContent}
          </span>
          {appearance === 'ellipsis' ? <VisuallyHidden>{announcement}</VisuallyHidden> : null}
        </>
      </Text>
    );
  },
);

GleamLabel.displayName = 'Gleam.Label';
